import React, { useCallback, useMemo, useState } from 'react'

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  QuestionCircleOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { List, Progress, DatePicker, Button, Popconfirm } from 'antd'
import { Link } from 'react-router-dom'

import { ReportType } from '~/pages/reports/shared/types'
import { healthReportTelemetry } from '~/telemetry'
import { formatDateTime } from '~/utils/date-time'
import { CloudUIProvider } from '~/utils/tenant-type'

import {
  useGetReportList,
  useCreateReport,
  type HealthReportListItem,
  useDeleteReport
} from './helpers'

enum ReportStatus {
  Pending = 'pending',
  Success = 'success',
  Running = 'running',
  Failed = 'failed'
}

enum DisplayStatus {
  Finished = 'finished',
  None = 'none',
  Creating = 'creating',
  Failed = 'failed'
}

enum ReportAction {
  View = 'view',
  Create = 'create',
  ReCreate = 'recreate',
  Delete = 'delete'
}

const itemRenderTable = [
  {
    compute: (item: HealthReportListItem) =>
      item.existed && item.status === ReportStatus.Success,
    status: DisplayStatus.Finished,
    icon: <CheckCircleOutlined className='text-cc-success' />,
    description:
      'The report is ready. If you find a problem, you can regenerate it.',
    actions: [ReportAction.View, ReportAction.ReCreate, ReportAction.Delete]
  },
  {
    compute: (item: HealthReportListItem) => !item.existed && !item.status,
    status: DisplayStatus.None,
    icon: <QuestionCircleOutlined className='text-cc-warning' />,
    description: 'No report available',
    actions: [ReportAction.Create]
  },
  {
    compute: (item: HealthReportListItem) =>
      !item.existed &&
      [ReportStatus.Pending, ReportStatus.Running].includes(
        item.status as ReportStatus
      ),
    status: DisplayStatus.Creating,
    icon: <SyncOutlined className='text-cc-primary' spin />,
    description: 'The report is being generated',
    actions: []
  },
  {
    compute: (item: HealthReportListItem) =>
      item.status === ReportStatus.Failed,
    status: DisplayStatus.Failed,
    icon: <CloseCircleOutlined className='text-cc-error' />,
    description: 'Failed to generate the report',
    actions: [ReportAction.ReCreate, ReportAction.Delete]
  }
]

const LIST_DEFAULT_INTERVAL = 0
const LIST_REFRESH_INTERVAL = 10 * 1000

interface HealthReportListProps {
  orgId: string
  clusterId: string
  startTime: number
  endTime: number
}

export const HealthReportList: React.FC<HealthReportListProps> = ({
  orgId,
  clusterId,
  startTime,
  endTime
}) => {
  const { data, isLoading, mutate } = useGetReportList(
    {
      orgId,
      clusterId,
      startTime,
      endTime
    },
    {
      refreshInterval: (data) => {
        if (!Array.isArray(data)) {
          return 0
        }

        const runningItem = data.find(
          (item) =>
            !item.existed &&
            [ReportStatus.Pending, ReportStatus.Running].includes(
              item.status as ReportStatus
            )
        )

        return runningItem ? LIST_REFRESH_INTERVAL : LIST_DEFAULT_INTERVAL
      }
    }
  )

  const { mutate: createReportAsync } = useCreateReport()
  const onGenerate = useCallback(
    async (itemId: string) => {
      // Update the cache immediately
      mutate(
        (data) =>
          data?.map((item) =>
            item.itemID === itemId
              ? {
                  ...item,
                  existed: false,
                  status: ReportStatus.Running,
                  progress: 0
                }
              : item
          ),
        {
          revalidate: false
        }
      )

      await createReportAsync({
        orgId,
        clusterId,
        itemId
      })
      // revalidate
      mutate()
    },
    [orgId, clusterId, createReportAsync, mutate]
  )

  const { mutate: deleteReport } = useDeleteReport()
  const onDelete = useCallback(
    async (rid: string) => {
      await deleteReport({
        oid: orgId,
        cid: clusterId,
        rid
      })
      // revalidate
      mutate()
    },
    [orgId, clusterId, deleteReport, mutate]
  )

  const reportList = useMemo(() => {
    return data.map((item) => {
      const title = [item.startTime, item.endTime]
        .map((sec) => formatDateTime(sec ?? 0))
        .join(' ~ ')

      const target = itemRenderTable.find((config) => config.compute(item))
      const description = target?.description ?? ''

      return {
        id: item.itemID!,
        title,
        status: target?.status!,
        process: item.progress ?? 0,
        icon: target?.icon!,
        description,
        actions: target?.actions!
      }
    })
  }, [data])

  const [timeRange, setTimeRange] = useState<[number, number] | null>(null)

  function handleChangeTimeRange(values: any) {
    if (values === null) {
      setTimeRange(null)
    } else {
      setTimeRange([values[0].unix(), values[1].unix()])
    }
  }

  function handleCreateReport() {
    healthReportTelemetry.generateCloudReport()
    createReportAsync(
      {
        orgId,
        clusterId,
        startTime: timeRange![0],
        endTime: timeRange![1]
      },
      function () {
        mutate()
      }
    )
  }

  return (
    <div>
      <CloudUIProvider>
        <div className='flex items-baseline py-4'>
          <span className='pr-2'>Time Range:</span>
          <DatePicker.RangePicker
            showTime
            className='rounded-lg'
            onChange={handleChangeTimeRange}
          />
          <Button
            type='primary'
            className='ml-auto'
            disabled={timeRange === null}
            onClick={handleCreateReport}
          >
            Create Report
          </Button>
        </div>
      </CloudUIProvider>

      <List
        loading={isLoading}
        dataSource={reportList}
        renderItem={(item) => {
          const actions = item.actions
            .map((action) => {
              switch (action) {
                case ReportAction.View:
                  return (
                    <Link
                      to={`report/${ReportType.Health}/${item.id}`}
                      target='_blank'
                      onClick={() => healthReportTelemetry.viewReport(item.id)}
                    >
                      View
                    </Link>
                  )
                case ReportAction.Create:
                  return (
                    <a
                      onClick={() => {
                        onGenerate(item.id)
                        healthReportTelemetry.generateOPReport(item.id)
                      }}
                    >
                      Generate
                    </a>
                  )
                case ReportAction.ReCreate:
                  return (
                    <a
                      onClick={() => {
                        onGenerate(item.id)
                        healthReportTelemetry.regenerateReport(item.id)
                      }}
                    >
                      Regenerate
                    </a>
                  )
                case ReportAction.Delete:
                  return (
                    <CloudUIProvider>
                      <Popconfirm
                        onConfirm={() => {
                          healthReportTelemetry.deleteReport(item.id)
                          return onDelete(item.id)
                        }}
                        title='Are you sure？'
                        okType='danger'
                        icon={
                          <ExclamationCircleFilled style={{ color: 'red' }} />
                        }
                      >
                        <Button className='px-0' type='link' danger>
                          Delete
                        </Button>
                      </Popconfirm>
                    </CloudUIProvider>
                  )
                default:
                  return null
              }
            })
            .filter((el) => el)

          const isFinished = item.status === DisplayStatus.Finished
          const isRunning = item.status === DisplayStatus.Creating

          return (
            <List.Item actions={actions}>
              <List.Item.Meta
                avatar={item.icon}
                title={
                  isFinished ? (
                    <Link
                      to={`report/${ReportType.Health}/${item.id}`}
                      target='_blank'
                      onClick={() => healthReportTelemetry.viewReport(item.id)}
                    >
                      {item.title}
                    </Link>
                  ) : (
                    item.title
                  )
                }
                description={item.description}
              />
              {isRunning && (
                <Progress
                  className='w-[100px]'
                  percent={item.process}
                  size='small'
                  status='active'
                />
              )}
            </List.Item>
          )
        }}
      />
    </div>
  )
}

export default HealthReportList
